/**
 * TokenManager - Secure token storage utility
 *
 * Security model (pentest remediation — sensitive data must not live in
 * localStorage):
 * - Auth tokens and user data are NEVER written to localStorage. localStorage
 *   persists indefinitely and is fully readable by any JavaScript, so a single
 *   XSS would hand an attacker a long-lived credential.
 * - Primary storage is in-memory — invisible to a stored payload that runs in
 *   a later page load.
 * - sessionStorage is the persistence fallback: it survives a page refresh but
 *   is cleared when the tab/browser closes, which bounds token lifetime.
 * - getToken()/getUser() still READ a legacy localStorage value if one exists
 *   so in-flight sessions are not broken — but they immediately migrate it
 *   into sessionStorage and delete the localStorage copy.
 * - A session that must survive a full browser restart should be implemented
 *   with an HttpOnly, Secure refresh-token cookie (a server-side change), not
 *   by persisting the bearer token in localStorage.
 */

// Storage keys
const STORAGE_KEYS = {
  TOKEN: 'helix_auth_token',
  USER: 'helix_auth_user',
  IMPERSONATING: 'helix_impersonating',
  ORIGINAL_TOKEN: 'helix_original_token',
  ORIGINAL_USER: 'helix_original_user',
  // CR-4858: Records which of the impersonated user's companies the admin
  // explicitly chose as the access context (e.g. "Login As" was clicked from
  // the Corex users tab, not from the user's primary company). Used by
  // ViewContext to pin the post-reload landing scope so the impersonated
  // session shows the company the admin was inspecting, not the user's
  // default. Only populated when the admin started impersonation through a
  // company-scoped surface; absent for global-impersonation flows.
  IMPERSONATION_CONTEXT: 'helix_impersonation_context',
  // CR-5316 rework: separate slot for GROUP-scoped impersonation context.
  // Set when the admin invoked Login As from a Supplier Group page — used
  // by ViewContext to land the post-reload session at GROUP view level
  // rather than at the COMPANY scope IMPERSONATION_CONTEXT carries.
  IMPERSONATION_GROUP_CONTEXT: 'helix_impersonation_group_context',
  AUDIT_LOG_ID: 'helix_impersonation_audit_log_id',
} as const;

// In-memory storage for tokens (most secure)
let memoryToken: string | null = null;
let memoryOriginalToken: string | null = null;

// The last token this browser saw rejected with a 401. Kept so a sibling tab
// can never hand it back over the BroadcastChannel session handoff: a tab
// that had just cleared a dead impersonated token asked its siblings for a
// session, got the same dead token, 401'd, cleared, asked again — a reload
// loop that only clearing storage or incognito would end. Survives clearAll()
// on purpose; a fresh login writes a different token.
const DEAD_TOKEN_KEY = 'helix_dead_token';
let memoryDeadToken: string | null = null;
const isDeadToken = (token: string | null | undefined): boolean => {
  if (!token) return false;
  if (memoryDeadToken === token) return true;
  try { return sessionStorage.getItem(DEAD_TOKEN_KEY) === token; } catch { return false; }
};

// "Remember me" preference flag. Retained for the login UI, but it no
// longer changes WHERE tokens are stored — auth material always lives in
// memory + sessionStorage and never in localStorage (pentest
// remediation). True cross-browser-restart persistence needs an HttpOnly
// cookie. The flag itself is a non-sensitive boolean.
const REMEMBER_KEY = 'helix_remember_me';

/**
 * Safe JSON parse that handles "undefined", null, and invalid JSON
 */
const safeJSONParse = <T,>(value: string | null): T | null => {
  if (!value || value === 'undefined' || value === 'null') {
    return null;
  }
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

/**
 * Check if a token string is valid (not corrupted)
 */
const isValidTokenString = (token: string | null): token is string => {
  return !!token && token !== 'undefined' && token !== 'null' && token.length > 0;
};

/**
 * TokenManager - Centralized secure token management
 */
export const TokenManager = {
  /**
   * Get the current auth token
   * Priority: memory > sessionStorage
   */
  getToken(): string | null {
    // First check memory (most secure)
    if (isValidTokenString(memoryToken)) {
      return memoryToken;
    }

    // Primary persistence: sessionStorage.
    const storedToken = sessionStorage.getItem(STORAGE_KEYS.TOKEN);
    if (isValidTokenString(storedToken)) {
      memoryToken = storedToken;
      return storedToken;
    }

    // Migration: a token may still be in localStorage from an older build
    // or a "remember me" login that pre-dates this fix. Read it once, move
    // it into sessionStorage, and purge the localStorage copy so auth
    // material never lingers in localStorage.
    const legacyLocalToken = localStorage.getItem(STORAGE_KEYS.TOKEN);
    if (isValidTokenString(legacyLocalToken)) {
      memoryToken = legacyLocalToken;
      sessionStorage.setItem(STORAGE_KEYS.TOKEN, legacyLocalToken);
      localStorage.removeItem(STORAGE_KEYS.TOKEN);
      return legacyLocalToken;
    }

    // Migration: legacy unprefixed localStorage key.
    const legacyToken = localStorage.getItem('token');
    if (isValidTokenString(legacyToken)) {
      memoryToken = legacyToken;
      sessionStorage.setItem(STORAGE_KEYS.TOKEN, legacyToken);
      localStorage.removeItem('token');
      return legacyToken;
    }

    return null;
  },

  /**
   * Set the auth token
   */
  setToken(token: string): void {
    if (!isValidTokenString(token)) {
      console.warn('TokenManager: Attempted to set invalid token');
      return;
    }
    memoryToken = token;
    sessionStorage.setItem(STORAGE_KEYS.TOKEN, token);
    // Defensive: never let a token copy survive in localStorage.
    localStorage.removeItem(STORAGE_KEYS.TOKEN);
    localStorage.removeItem('token');
  },

  /**
   * CR-6282: snapshot the auth material (token, user, impersonation context) for
   * a cross-tab handoff. Returns null when this tab is not authenticated. The
   * snapshot is passed in-memory over BroadcastChannel (see lib/sessionSync.ts)
   * so a tab opened from a drill-down ``target="_blank"`` hyperlink can adopt the
   * live session instead of being bounced to /login. It is never persisted — the
   * material stays exactly as accessible as it already is in sessionStorage.
   */
  exportSession(): Record<string, string> | null {
    const token = sessionStorage.getItem(STORAGE_KEYS.TOKEN);
    if (!isValidTokenString(token) || isDeadToken(token)) return null;
    const snapshot: Record<string, string> = {};
    for (const key of Object.values(STORAGE_KEYS)) {
      const value = sessionStorage.getItem(key);
      if (value !== null) snapshot[key] = value;
    }
    return snapshot;
  },

  /**
   * CR-6282: restore an auth snapshot received from another tab into this tab's
   * sessionStorage. Only accepts a snapshot carrying a valid token, and only
   * writes the known auth keys. Returns true when a session was adopted.
   */
  importSession(snapshot: Record<string, string> | null | undefined): boolean {
    if (!snapshot || !isValidTokenString(snapshot[STORAGE_KEYS.TOKEN])) return false;
    // A sibling offering the token this tab already saw die is the loop.
    if (isDeadToken(snapshot[STORAGE_KEYS.TOKEN])) return false;
    const allowed = new Set<string>(Object.values(STORAGE_KEYS));
    for (const [key, value] of Object.entries(snapshot)) {
      if (allowed.has(key)) sessionStorage.setItem(key, value);
    }
    memoryToken = snapshot[STORAGE_KEYS.TOKEN];
    // Keep the pentest posture — no token copy survives in localStorage, incl.
    // the legacy unprefixed key that getToken/setToken also migrate and purge.
    localStorage.removeItem(STORAGE_KEYS.TOKEN);
    localStorage.removeItem('token');
    return true;
  },

  /**
   * Clear the auth token
   */
  /** Record a token the server has rejected, so no handoff can revive it. */
  markTokenDead(token: string | null | undefined): void {
    if (!isValidTokenString(token ?? null)) return;
    memoryDeadToken = token as string;
    try { sessionStorage.setItem(DEAD_TOKEN_KEY, token as string); } catch { /* storage unavailable */ }
  },
  isTokenDead(token: string | null | undefined): boolean {
    return isDeadToken(token);
  },
  clearToken(): void {
    memoryToken = null;
    // Clear from both storages
    sessionStorage.removeItem(STORAGE_KEYS.TOKEN);
    localStorage.removeItem(STORAGE_KEYS.TOKEN);
    localStorage.removeItem('token');
  },

  /**
   * Get user data from storage
   */
  getUser<T>(): T | null {
    const userData = sessionStorage.getItem(STORAGE_KEYS.USER);
    if (userData) {
      return safeJSONParse<T>(userData);
    }

    // Migration: move any legacy localStorage copy into sessionStorage,
    // then purge it so user data never lingers in localStorage.
    const legacyLocalUser = localStorage.getItem(STORAGE_KEYS.USER);
    if (legacyLocalUser) {
      const parsed = safeJSONParse<T>(legacyLocalUser);
      if (parsed) {
        sessionStorage.setItem(STORAGE_KEYS.USER, legacyLocalUser);
        localStorage.removeItem(STORAGE_KEYS.USER);
        return parsed;
      }
    }

    // Migration from legacy unprefixed key.
    const legacyUser = localStorage.getItem('user');
    if (legacyUser) {
      const parsed = safeJSONParse<T>(legacyUser);
      if (parsed) {
        sessionStorage.setItem(STORAGE_KEYS.USER, legacyUser);
        localStorage.removeItem('user');
        return parsed;
      }
    }

    return null;
  },

  /**
   * Set user data
   */
  setUser<T>(user: T): void {
    sessionStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
    // Defensive: never let user data survive in localStorage.
    localStorage.removeItem(STORAGE_KEYS.USER);
    localStorage.removeItem('user');
  },

  /**
   * Clear user data
   */
  clearUser(): void {
    sessionStorage.removeItem(STORAGE_KEYS.USER);
    localStorage.removeItem(STORAGE_KEYS.USER);
    localStorage.removeItem('user');
  },

  /**
   * Check if currently impersonating
   */
  isImpersonating(): boolean {
    return sessionStorage.getItem(STORAGE_KEYS.IMPERSONATING) === 'true' ||
           localStorage.getItem('isImpersonating') === 'true';
  },

  /**
   * Get original token (for impersonation)
   */
  getOriginalToken(): string | null {
    if (isValidTokenString(memoryOriginalToken)) {
      return memoryOriginalToken;
    }

    const storedToken = sessionStorage.getItem(STORAGE_KEYS.ORIGINAL_TOKEN);
    if (isValidTokenString(storedToken)) {
      memoryOriginalToken = storedToken;
      return storedToken;
    }

    // Migration from localStorage
    const legacyToken = localStorage.getItem('originalToken');
    if (isValidTokenString(legacyToken)) {
      memoryOriginalToken = legacyToken;
      sessionStorage.setItem(STORAGE_KEYS.ORIGINAL_TOKEN, legacyToken);
      localStorage.removeItem('originalToken');
      return legacyToken;
    }

    return null;
  },

  /**
   * Get original user (for impersonation)
   */
  getOriginalUser<T>(): T | null {
    const sessionUser = sessionStorage.getItem(STORAGE_KEYS.ORIGINAL_USER);
    if (sessionUser) {
      return safeJSONParse<T>(sessionUser);
    }

    // Migration from localStorage
    const legacyUser = localStorage.getItem('originalUser');
    if (legacyUser) {
      const userData = safeJSONParse<T>(legacyUser);
      if (userData) {
        sessionStorage.setItem(STORAGE_KEYS.ORIGINAL_USER, legacyUser);
        localStorage.removeItem('originalUser');
        return userData;
      }
    }

    return null;
  },

  /**
   * Start impersonation - store original user/token
   */
  startImpersonation<T>(originalUser: T, originalToken: string, auditLogId?: number): void {
    // Refuse to overwrite a parked credential. Starting a second session from
    // inside one used to replace the admin's original token/user with the
    // impersonated user's, so "Stop impersonation" returned to the middle of
    // the chain and the admin — whose token is still alive server-side —
    // became unreachable, because this was the only reference to it. The
    // backend now refuses to mint a nested session at all; this is the second
    // line of defence, and it keeps the admin recoverable even if a caller
    // reaches here some other way.
    if (sessionStorage.getItem(STORAGE_KEYS.ORIGINAL_TOKEN)) {
      return;
    }
    memoryOriginalToken = originalToken;
    sessionStorage.setItem(STORAGE_KEYS.IMPERSONATING, 'true');
    sessionStorage.setItem(STORAGE_KEYS.ORIGINAL_TOKEN, originalToken);
    sessionStorage.setItem(STORAGE_KEYS.ORIGINAL_USER, JSON.stringify(originalUser));
    // A previous session's context must not bleed into this one. Callers that
    // know a company/group set it right after this call; callers that don't
    // (the plain Users-tab Login As) would otherwise inherit whatever the last
    // impersonation pinned.
    sessionStorage.removeItem(STORAGE_KEYS.IMPERSONATION_CONTEXT);
    sessionStorage.removeItem(STORAGE_KEYS.IMPERSONATION_GROUP_CONTEXT);
    sessionStorage.removeItem(STORAGE_KEYS.AUDIT_LOG_ID);
    if (auditLogId) {
      sessionStorage.setItem(STORAGE_KEYS.AUDIT_LOG_ID, String(auditLogId));
    }
  },

  /**
   * CR-4858: Persist the admin-chosen company context for an impersonation
   * session. Set before the post-impersonation reload so ViewContext can
   * read it on rehydration and pin the landing scope to that company.
   */
  setImpersonationContext(context: { id: number; name: string }): void {
    sessionStorage.setItem(STORAGE_KEYS.IMPERSONATION_CONTEXT, JSON.stringify(context));
  },

  /**
   * CR-4858: Read the admin-chosen company context for the active
   * impersonation session, or null if none was set (global Login As flows).
   */
  getImpersonationContext(): { id: number; name: string } | null {
    return safeJSONParse<{ id: number; name: string }>(
      sessionStorage.getItem(STORAGE_KEYS.IMPERSONATION_CONTEXT)
    );
  },

  /**
   * CR-5316 rework: Persist the admin-chosen GROUP context for an
   * impersonation session. Set when Login As was invoked from a Supplier
   * Group page so ViewContext lands the impersonated session at GROUP
   * view-level instead of falling through to the COMPANY-scoped path.
   */
  setImpersonationGroupContext(context: { id: number; name: string }): void {
    sessionStorage.setItem(
      STORAGE_KEYS.IMPERSONATION_GROUP_CONTEXT,
      JSON.stringify(context),
    );
  },

  /**
   * CR-5316 rework: Read the admin-chosen GROUP context for the active
   * impersonation session, or null if none was set.
   */
  getImpersonationGroupContext(): { id: number; name: string } | null {
    return safeJSONParse<{ id: number; name: string }>(
      sessionStorage.getItem(STORAGE_KEYS.IMPERSONATION_GROUP_CONTEXT)
    );
  },

  /**
   * Stop impersonation - clear original user/token
   */
  stopImpersonation(): void {
    memoryOriginalToken = null;
    sessionStorage.removeItem(STORAGE_KEYS.IMPERSONATING);
    sessionStorage.removeItem(STORAGE_KEYS.ORIGINAL_TOKEN);
    sessionStorage.removeItem(STORAGE_KEYS.ORIGINAL_USER);
    sessionStorage.removeItem(STORAGE_KEYS.IMPERSONATION_CONTEXT);
    sessionStorage.removeItem(STORAGE_KEYS.IMPERSONATION_GROUP_CONTEXT);
    sessionStorage.removeItem(STORAGE_KEYS.AUDIT_LOG_ID);
    // Clean up legacy localStorage
    localStorage.removeItem('isImpersonating');
    localStorage.removeItem('originalToken');
    localStorage.removeItem('originalUser');
  },

  getAuditLogId(): number | null {
    const val = sessionStorage.getItem(STORAGE_KEYS.AUDIT_LOG_ID);
    return val ? parseInt(val, 10) : null;
  },

  /**
   * Record the "remember me" preference (a non-sensitive boolean flag).
   *
   * This NO LONGER changes where tokens are stored — auth tokens always
   * live in memory + sessionStorage and never in localStorage (pentest
   * remediation). The flag is kept only so the login UI can round-trip
   * the checkbox state; genuine cross-browser-restart persistence would
   * require an HttpOnly refresh-token cookie.
   */
  setRememberMe(remember: boolean): void {
    if (remember) {
      localStorage.setItem(REMEMBER_KEY, 'true');
    } else {
      localStorage.removeItem(REMEMBER_KEY);
    }
  },

  clearAll(): void {
    memoryToken = null;
    memoryOriginalToken = null;

    // Clear both storages
    sessionStorage.removeItem(STORAGE_KEYS.TOKEN);
    sessionStorage.removeItem(STORAGE_KEYS.USER);
    sessionStorage.removeItem(STORAGE_KEYS.IMPERSONATING);
    sessionStorage.removeItem(STORAGE_KEYS.ORIGINAL_TOKEN);
    sessionStorage.removeItem(STORAGE_KEYS.ORIGINAL_USER);
    sessionStorage.removeItem(STORAGE_KEYS.IMPERSONATION_CONTEXT);
    // These two were missing, so they outlived a sign-out: a later Login As
    // started from a surface that sets no group context inherited the previous
    // session's, and ViewContext pinned the new session to that group; the
    // stale audit id was then posted to stop-impersonate for an unrelated
    // session. stopImpersonation() has always cleared both — clearAll() is a
    // superset of it and must too.
    sessionStorage.removeItem(STORAGE_KEYS.IMPERSONATION_GROUP_CONTEXT);
    sessionStorage.removeItem(STORAGE_KEYS.AUDIT_LOG_ID);

    localStorage.removeItem(STORAGE_KEYS.TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER);
    localStorage.removeItem(REMEMBER_KEY);

    // Clean up legacy items
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('isImpersonating');
    localStorage.removeItem('originalToken');
    localStorage.removeItem('originalUser');
  },

  /**
   * Check if there's a valid token stored
   */
  hasToken(): boolean {
    return isValidTokenString(this.getToken());
  },
};

export default TokenManager;
